/**
 * ClientAPAnalytics — Client-portal AP analytics (scoped operational report)
 *
 * Mirrors the internal operational CA report (ActionPlanReportPage) exactly
 * on metric definitions, but scoped to the signed-in client user's site
 * grants instead of auditor job assignment:
 *
 *   L1 (group grant)       — sees every site under the assigned group(s)
 *                            — portfolio-wide.
 *   L2 (sub-region grant)  — sees every site under the assigned
 *                            sub-region(s) — regional.
 *   L3 (direct site grant) — sees their directly-assigned site(s).
 *
 * Scope is enforced server-side (GET /api/client/action-plans/analytics)
 * for rows, totals, filter choices (site_choices) and the CSV export —
 * never client-side filtering of a wider payload.
 *
 * L1/L2 remain read-only everywhere else in the portal; L3 retains full
 * response/action rights.  This analytics view is informational for all
 * three tiers and does not itself grant or restrict any mutation.
 *
 * Metric definitions (identical to the internal CA report):
 *   outstanding             — auditor_assessment != 'implemented'
 *   overdue                 — outstanding AND by_when < today (independent)
 *   awaiting_verification   — client completed, auditor verdict pending
 *                             (independent; "pending verification")
 *   verified_closed         — auditor_assessment == 'implemented' only
 *   original_age_days       — days since first creation; null = "Unknown"
 *   verification_delay_days — assessed_at − client_completed_at (verified rows only)
 *   total_closure_days      — assessed_at − original creation (verified rows only)
 * Negative intervals are never clamped to zero — shown as "Unknown".
 *
 * Route: /client/action-plans/analytics
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronRight, Loader2, RotateCcw } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { clientActionPlanService } from '../services';
import { getApiError } from '@/lib/utils';
import { useClientPortal } from '../contexts/ClientPortalContext';

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (v) => {
  if (!v) return 'Unknown';
  try { return new Date(v).toLocaleDateString('en-GB'); } catch { return 'Unknown'; }
};

const fmtDays = (v) => {
  if (v === null || v === undefined) return 'Unknown';
  if (v === 0) return '< 1 day';
  return `${v}d`;
};

const ROLE_LABEL = { L3: 'Site', L2: 'Regional', L1: 'Group', NONE: '—' };

const STATE_LABELS = {
  overdue:               'Overdue',
  awaiting_verification: 'Completed and awaiting auditor review',
  not_implemented:       'To do',
  actionable:            'To do',
  implemented:           'Verified complete',
};



function RoleTag({ role }) {
  if (!role || role === 'NONE') return null;
  return (
    <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600 border border-slate-200">
      {ROLE_LABEL[role] || role}
    </span>
  );
}

// ── Summary card ─────────────────────────────────────────────────────────────

const STATE_FILTER_LABELS = {
  outstanding: 'Outstanding',
  overdue: 'Overdue',
  awaiting_verification: 'Completed and awaiting auditor review',
  verified: 'Verified complete',
};

// ── Headline state badge (Issue 6) ────────────────────────────────────────────
// Three headline states replace the previous raw ca_state labels:
//   Verified complete                    — auditor confirmed
//   Completed and awaiting auditor review — client done, no verdict
//   To do                                — all other unresolved
// Overdue is a separate cross-cutting warning badge (deadline, not a state).

const HEADLINE_STYLE = {
  verified:  'bg-green-100 text-green-800',
  awaiting:  'bg-amber-100 text-amber-800',
  todo:      'bg-blue-100 text-blue-800',
};
const HEADLINE_LABEL = {
  verified: 'Verified complete',
  awaiting: 'Completed and awaiting auditor review',
  todo:     'To do',
};

function StateBadge({ ca }) {
  const isVerified = ca.auditor_assessment === 'implemented';
  const isAwaiting = ca.awaiting_verification && !isVerified;
  const headlineKey = isVerified ? 'verified' : isAwaiting ? 'awaiting' : 'todo';
  const isOverdue   = ca.is_overdue && !isVerified;

  return (
    <div className="flex flex-col gap-0.5 items-start">
      <span
        data-testid={`analytics-state-badge-${ca.ca_id}`}
        className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${HEADLINE_STYLE[headlineKey]}`}
      >
        {HEADLINE_LABEL[headlineKey]}
      </span>
      {isOverdue && (
        <span
          data-testid={`analytics-overdue-badge-${ca.ca_id}`}
          className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800"
        >
          Overdue
        </span>
      )}
    </div>
  );
}

// ── Summary card ─────────────────────────────────────────────────────────────

function SummaryCard({ label, value, sub, highlight, testId, onClick }) {
  return (
    <div
      data-testid={testId}
      onClick={onClick}
      className={`rounded-lg border p-4 flex flex-col gap-1 ${highlight ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'} ${onClick ? 'cursor-pointer hover:border-indigo-300 transition-colors' : ''}`}
    >
      <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</span>
      <span className={`text-3xl font-bold ${highlight ? 'text-red-700' : 'text-gray-900'}`}>
        {value ?? '—'}
      </span>
      {sub && <span className="text-xs text-gray-500">{sub}</span>}
    </div>
  );
}

// ── Drill-down row ────────────────────────────────────────────────────────────

function CARow({ ca }) {
  const [open, setOpen] = useState(false);
  const isVerified = ca.auditor_assessment === 'implemented';

  return (
    <>
      <tr
        className="hover:bg-gray-50 cursor-pointer border-b border-gray-100"
        onClick={() => setOpen((v) => !v)}
        data-testid={`analytics-ca-row-${ca.ca_id}`}
      >
        <td className="px-3 py-2 text-sm text-gray-500 w-4">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </td>
        <td className="px-3 py-2 text-sm text-gray-700 max-w-xs truncate" title={ca.description}>
          {ca.description || '—'}
        </td>
        <td className="px-3 py-2 text-sm text-gray-600">
          <div className="flex items-center gap-1.5">
            {ca.site_name || '—'}
            <RoleTag role={ca.ap_role} />
          </div>
        </td>
        <td className="px-3 py-2 text-sm text-gray-600">{ca.scope || '—'}</td>
        <td className="px-3 py-2 text-sm"><StateBadge ca={ca} /></td>
        <td className="px-3 py-2 text-sm text-gray-600">{fmtDate(ca.by_when)}</td>
        <td className="px-3 py-2 text-sm text-gray-600">{fmtDays(ca.original_age_days)}</td>
        <td className="px-3 py-2 text-sm text-gray-600">
          {isVerified ? fmtDays(ca.verification_delay_days) : '—'}
        </td>
        <td className="px-3 py-2 text-sm">
          <Link
            to={`/client/action-plans/${ca.plan_id}`}
            className="text-indigo-600 hover:underline text-xs"
            onClick={(e) => e.stopPropagation()}
            data-testid={`analytics-plan-link-${ca.ca_id}`}
          >
            View plan →
          </Link>
        </td>
      </tr>
      {open && (
        <tr className="bg-gray-50 border-b border-gray-100" data-testid={`analytics-ca-detail-${ca.ca_id}`}>
          <td />
          <td colSpan={8} className="px-4 py-3">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-1 text-sm text-gray-700">
              {/* Issue 318: stock_number / internal AP reference suppressed in client portal — traceability preserved server-side */}
              <div><span className="font-medium text-gray-500">Audit date: </span>{fmtDate(ca.audit_date)}</div>
              <div><span className="font-medium text-gray-500">By who: </span>{ca.by_who || 'Unknown'}</div>
              <div>
                <span className="font-medium text-gray-500">Action history: </span>
                {ca.cf_generation === 0 ? 'Original action' : 'Carried forward'}
              </div>
              <div>
                <span className="font-medium text-gray-500">Client completion: </span>
                {ca.client_completion_status === 'implemented' ? 'Completed' :
                 ca.client_completion_status === 'partially_implemented' ? 'Partially done' :
                 ca.client_completion_status === 'not_implemented' ? 'Not done' :
                 ca.client_completion_status || '—'}
                {ca.client_completed_at ? ` (${fmtDate(ca.client_completed_at)})` : ''}
              </div>
              {ca.auditor_assessment && (
                <>
                  <div>
                    <span className="font-medium text-gray-500">Auditor verdict: </span>
                    {ca.auditor_assessment === 'implemented' ? 'Verified complete' :
                     ca.auditor_assessment === 'not_implemented' ? 'Not done' :
                     ca.auditor_assessment === 'partially_implemented' ? 'Partially done' :
                     ca.auditor_assessment}
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Assessed: </span>
                    {fmtDate(ca.assessed_at)}
                    {/* Issue 5: suppress system-generated actor labels */}
                    {ca.assessed_by_name && !/\[(FIXTURE|SYNTHETIC|AUTOMATED|SYSTEM)\]/i.test(ca.assessed_by_name)
                      ? ` by ${ca.assessed_by_name}` : ''}
                  </div>
                  {isVerified && (
                    <div>
                      <span className="font-medium text-gray-500">Verification delay: </span>
                      {fmtDays(ca.verification_delay_days)}
                      {' · '}
                      <span className="font-medium text-gray-500">Total closure: </span>
                      {fmtDays(ca.total_closure_days)}
                    </div>
                  )}
                  {ca.auditor_assessment_note && (
                    <div className="col-span-2 md:col-span-3">
                      <span className="font-medium text-gray-500">Note: </span>
                      {ca.auditor_assessment_note}
                    </div>
                  )}
                </>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ClientAPAnalytics() {
  const navigate = useNavigate();
  const { apEnabled, apOverallRole } = useClientPortal();
  const [searchParams, setSearchParams] = useSearchParams();

  // Direct-URL guard: redirect to dashboard when AP is disabled.
  useEffect(() => {
    if (apEnabled === false) navigate('/client/dashboard', { replace: true });
  }, [apEnabled, navigate]);

  // All hooks must be declared before any conditional return.
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  // Store region_choices in a ref so the load callback doesn't depend on `data`
  // and avoids an infinite re-render loop (data change → load identity change →
  // effect re-fires → setLoading(true) → spinner never resolves).
  const regionChoicesRef = useRef([]);

  const [siteId, setSiteId]       = useState(searchParams.get('site_id') || '');
  const [scope, setScope]         = useState(searchParams.get('scope') || '');
  const [stateFilter, setStateFilter] = useState(searchParams.get('state') || '');
  // L1 portfolio region filter (region_id string or '')
  const [regionFilter, setRegionFilter] = useState('');

  // Region/sub-region scope carried from a dashboard drill-down (e.g. a
  // selected region's AP Oversight tile). Always re-sent on every analytics
  // call for this page visit — the backend intersects it with the caller's
  // true accessible scope server-side, it is never trusted on its own.
  const regionSiteIdsParam = searchParams.get('site_ids');
  const regionLabel        = searchParams.get('region_label');
  const regionSiteIds = useMemo(
    () => regionSiteIdsParam
      ? regionSiteIdsParam.split(',').map(Number).filter(n => !Number.isNaN(n))
      : null,
    [regionSiteIdsParam]
  );

  const from = searchParams.get('from');
  // L1/L2 users have no task-list — when arriving without an explicit from param,
  // their natural "back" destination is the dashboard.
  const _isAnalyticsRole = apOverallRole === 'L1' || apOverallRole === 'L2';
  const backTo = from === 'dashboard'
    ? '/client/dashboard'
    : (from === 'property' && searchParams.get('site_id'))
      ? `/client/property-dashboard?site=${searchParams.get('site_id')}`
      : _isAnalyticsRole
        ? '/client/dashboard'
        : '/client/action-plans';
  const backLabel = from === 'dashboard' ? 'Back to Dashboard'
    : from === 'property' ? 'Back to Property Dashboard'
    : _isAnalyticsRole ? 'Back to Dashboard'
    : 'Back to Action Plans';

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = {};
    if (siteId) params.site_id = Number(siteId);
    if (scope)  params.scope   = scope;

    // Dashboard drill-down site_ids and L1 region filter: compute intersection
    // before sending. Backend enforces its own scope intersection on top.
    let effectiveSiteIds = regionSiteIds ? [...regionSiteIds] : [];
    if (regionFilter) {
      const selectedRegion = regionChoicesRef.current.find(
        r => String(r.region_id) === regionFilter
      );
      if (selectedRegion?.site_ids?.length) {
        if (effectiveSiteIds.length > 0) {
          const rSet = new Set(selectedRegion.site_ids);
          effectiveSiteIds = effectiveSiteIds.filter(id => rSet.has(id));
        } else {
          effectiveSiteIds = selectedRegion.site_ids;
        }
      }
    }
    if (effectiveSiteIds.length) params.site_ids = effectiveSiteIds.join(',');

    clientActionPlanService.analytics(params)
      .then(d => {
        setData(d);
        if (d?.region_choices) regionChoicesRef.current = d.region_choices;
      })
      .catch(e => setError(getApiError(e)))
      .finally(() => setLoading(false));
  }, [siteId, scope, regionSiteIds, regionFilter]);

  useEffect(() => { load(); }, [load]);

  const clearStateFilter = () => {
    setStateFilter('');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('state');
      return next;
    });
  };

  const s = data?.summary;
  const rows = data?.rows ?? [];
  const siteChoices = data?.site_choices ?? [];
  const regionChoices = data?.region_choices ?? [];
  const overallRole = data?.overall_role ?? apOverallRole ?? null;
  const scopeChoices = [...new Set(rows.map(r => r.scope).filter(Boolean))].sort();

  // When an L1 region filter is active, narrow the site dropdown to that region's sites.
  const selectedRegionObj = regionChoices.find(r => String(r.region_id) === regionFilter);
  const regionSiteIdSet   = selectedRegionObj ? new Set(selectedRegionObj.site_ids) : null;
  const effectiveSiteChoices = regionSiteIdSet
    ? siteChoices.filter(s => regionSiteIdSet.has(s.site_id))
    : siteChoices;

  const filteredRows = useMemo(() => {
    switch (stateFilter) {
      case 'outstanding':            return rows.filter(r => r.auditor_assessment !== 'implemented');
      case 'overdue':                return rows.filter(r => r.is_overdue);
      case 'awaiting_verification':  return rows.filter(r => r.awaiting_verification);
      case 'verified':                return rows.filter(r => r.auditor_assessment === 'implemented');
      default:                       return rows;
    }
  }, [rows, stateFilter]);

  // Loading gate: return nothing while branding resolves to prevent flash before
  // redirect (apEnabled===false handled by the guard useEffect above).
  if (apEnabled === null) return null;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6" data-testid="client-ap-analytics-page">

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link
            to={backTo}
            className="text-slate-400 hover:text-slate-600 transition-colors"
            data-testid="analytics-back-link"
            title={backLabel}
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Action Plan Analytics</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Scoped to your sites — figures match what your audit team records.
            </p>
          </div>
        </div>
      </div>

      {/* Region scope breadcrumb — carried from a dashboard region drill-down.
          Non-dismissible from the dropdowns below (which stay full-portfolio
          choices); "View full portfolio" clears it explicitly. */}
      {regionSiteIds?.length > 0 && (
        <div
          className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2"
          data-testid="analytics-region-scope-chip"
        >
          <span>
            Scoped to: <strong>{regionLabel || `${regionSiteIds.length} sites`}</strong> (from dashboard)
          </span>
          <Link
            to="/client/action-plans/analytics"
            className="ml-auto text-emerald-600 hover:text-emerald-900 font-medium"
            data-testid="analytics-region-scope-clear-link"
          >
            View full portfolio
          </Link>
        </div>
      )}

      {/* Filter bar */}
      <div
        className="flex flex-wrap gap-3 items-end bg-white border border-slate-200 rounded-lg p-4"
        data-testid="analytics-filters"
      >
        {/* L1-only Region filter */}
        {overallRole === 'L1' && regionChoices.length > 0 && (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-600">Region</label>
            <select
              data-testid="analytics-filter-region"
              value={regionFilter}
              onChange={(e) => {
                setRegionFilter(e.target.value);
                // If currently-selected site is outside this region, clear it
                if (e.target.value) {
                  const ro = regionChoices.find(r => String(r.region_id) === e.target.value);
                  if (ro && siteId && !ro.site_ids.includes(Number(siteId))) {
                    setSiteId('');
                  }
                }
              }}
              className="text-sm border border-slate-300 rounded px-2 py-1.5 min-w-[160px]"
            >
              <option value="">All regions</option>
              {regionChoices.map((r) => (
                <option key={r.region_id} value={r.region_id}>{r.region_name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-600">Site</label>
          <select
            data-testid="analytics-filter-site"
            value={siteId}
            onChange={(e) => setSiteId(e.target.value)}
            className="text-sm border border-slate-300 rounded px-2 py-1.5 min-w-[180px]"
          >
            <option value="">All accessible sites</option>
            {effectiveSiteChoices.map((s) => (
              <option key={s.site_id} value={s.site_id}>{s.site_name}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-600">Scope / Service</label>
          <select
            data-testid="analytics-filter-scope"
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            className="text-sm border border-slate-300 rounded px-2 py-1.5 min-w-[140px]"
          >
            <option value="">All scopes</option>
            {scopeChoices.map((sc) => (
              <option key={sc} value={sc}>{sc}</option>
            ))}
          </select>
        </div>

        <button
          data-testid="analytics-filter-reset-btn"
          onClick={() => { setSiteId(''); setScope(''); setRegionFilter(''); }}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50"
        >
          <RotateCcw size={13} /> Reset
        </button>
      </div>

      {/* State filter chip — set by clicking a summary card or arriving from
          a dashboard oversight link; narrows the rows table only (same rows,
          same definitions, client-side filter of an already-correct set). */}
      {stateFilter && (
        <div
          className="flex items-center gap-2 text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2"
          data-testid="analytics-state-filter-chip"
        >
          <span>
            Filtered: <strong>{STATE_FILTER_LABELS[stateFilter] || stateFilter}</strong>{' '}
            ({filteredRows.length} of {rows.length})
          </span>
          <button
            onClick={clearStateFilter}
            className="ml-auto text-indigo-500 hover:text-indigo-800 font-medium"
            data-testid="analytics-state-filter-clear-btn"
          >
            Clear filter
          </button>
        </div>
      )}

      {/* Summary cards */}
      {s && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="analytics-summary">
          <SummaryCard
            testId="analytics-summary-outstanding"
            label="Outstanding"
            value={s.outstanding}
            sub={s.original_age_days_median != null
              ? `Median age: ${fmtDays(Math.round(s.original_age_days_median))}`
              : 'Median age: Unknown'}
            onClick={() => setStateFilter('outstanding')}
          />
          <SummaryCard
            testId="analytics-summary-overdue"
            label="Overdue"
            value={s.overdue}
            highlight={s.overdue > 0}
            onClick={() => setStateFilter('overdue')}
          />
          <SummaryCard
            testId="analytics-summary-awaiting"
            label="Completed and awaiting auditor review"
            value={s.awaiting_verification}
            sub={s.awaiting_verification_age_days_median != null
              ? `Median wait: ${fmtDays(Math.round(s.awaiting_verification_age_days_median))}`
              : 'Median wait: Unknown'}
            onClick={() => setStateFilter('awaiting_verification')}
          />
          <SummaryCard
            testId="analytics-summary-verified"
            label="Verified complete"
            value={s.verified_closed}
            sub={s.verification_delay_days_median != null
              ? `Median verify delay: ${fmtDays(Math.round(s.verification_delay_days_median))}`
              : 'Median verify delay: Unknown'}
            onClick={() => setStateFilter('verified')}
          />
          <SummaryCard
            testId="analytics-summary-verified-30d"
            label="Verified — Last 30 Days"
            value={s.verified_closed_last_30d}
            sub="Closure throughput, not a rate"
          />
        </div>
      )}

      {/* Raised vs. Verified trend — last 6 calendar months. TRUTHFULNESS:
          this is the CURRENT cohort's latest known state by month, built
          from recorded creation/verification timestamps and the CURRENT
          implemented flag only — not a full historical event ledger. A
          corrective action re-assessed more than once shows only its
          latest verdict/date; an earlier verification later superseded is
          not separately represented. Carry-forward copies are never
          counted as newly raised. Future-dated timestamps are excluded. */}
      {s?.raised_vs_verified_trend_coverage &&
       !s.raised_vs_verified_trend_coverage.has_raised_history &&
       !s.raised_vs_verified_trend_coverage.has_verified_history ? (
        <div className="bg-white border border-slate-200 rounded-lg p-4 text-xs text-slate-400" data-testid="analytics-trend-insufficient">
          Insufficient recorded history to show a raised/verified trend for this scope.
        </div>
      ) : s?.raised_vs_verified_trend?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="analytics-trend-chart">
          <p className="text-xs font-semibold text-slate-600 mb-0.5">Raised vs. Verified — Last 6 Months</p>
          <p className="text-[11px] text-slate-400 mb-3">
            Current known state only, not a complete historical workflow log — each action shows its latest recorded verdict/date; an earlier verification later superseded is not separately counted. Carry-forward copies are never counted as newly raised. Future-dated entries are excluded.
          </p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={s.raised_vs_verified_trend} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="raised" name="Raised" fill="#94a3b8" radius={[3, 3, 0, 0]} />
              <Bar dataKey="verified" name="Verified" fill="#22c55e" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Definitions footnote */}
      <div className="text-xs text-slate-400 bg-slate-50 border border-slate-100 rounded px-3 py-2 space-y-0.5">
        <p>
          <strong>Outstanding</strong>: not verified closed.{' '}
          <strong>Overdue</strong>: outstanding and past target date (independent).{' '}
          <strong>Completed and awaiting auditor review</strong>: you&apos;ve recorded completion, auditor verdict not yet given (independent).{' '}
          <strong>Verified complete</strong>: auditor verdict = Verified complete only.
        </p>
        <p>
          <strong>Original age</strong>: days since first raised.{' '}
          <strong>Verification delay</strong>: days from your completion to auditor verification (verified rows only).{' '}
          Unavailable dates shown as &quot;Unknown&quot; — never zero or fabricated.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700" data-testid="analytics-error">
          {error}
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-12 text-slate-400" data-testid="analytics-loading">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      )}

      {!loading && !error && data && (
        <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="analytics-table">
          {filteredRows.length === 0 ? (
            <div className="text-sm text-slate-400 py-8 text-center" data-testid="analytics-empty">
              {stateFilter
                ? 'No corrective actions match this filter.'
                : 'No corrective actions match the current filters.'}
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="w-4" />
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Description</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Site</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Scope</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Target date</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Age (d)</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Verify delay (d)</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Plan</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((ca) => (
                  <CARow key={ca.ca_id} ca={ca} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
